import React, { useEffect, useMemo, useState } from 'react';
import { ColumnApi, GridApi, IDetailCellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { TranscriptionsAdminService } from '$/services/TranscriptionsAdminService';
import BixButton from '$/components/global/bixButton';
import CreateApprovalTask from '$/components/transcripts-grid/CreateApprovalTask';
import Modal from '$/components/global/modal';
import PublishButton from '$/components/grid/PublishButton';
import * as Bixby from '$/core';
import './index.scss';

interface ITranscriptsProps {
  companyId?: string;
  isCurrentCompanyMnpi?: boolean;
  isCreditSightsTranscriptionsIntegrationEnabled?: boolean;
  dispatchToastError?: (message: string) => Promise<void>;
  dispatchToastSuccess?: (message: string) => Promise<void>;
}

const TranscriptsGrid: React.FC<ITranscriptsProps> = props => {
  const {
    companyId,
    isCurrentCompanyMnpi,
    isCreditSightsTranscriptionsIntegrationEnabled,
    dispatchToastError,
    dispatchToastSuccess,
  } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isLoading, setIsLoading] = useState(false);
  const [transcriptionsError, setTranscriptionsError] = useState(undefined as string | undefined);
  const [transcriptions, setTranscriptions] = useState([] as Bixby.Models.AdminCompanyTranscription[]);
  const [isCreatingApprovalTask, setIsCreatingApprovalTask] = useState(false);
  const [currentTranscription, setCurrentTranscription] = useState(
    undefined as Bixby.Models.AdminCompanyTranscription | undefined
  );
  const [publishModalActive, setPublishModalActive] = useState(false);
  const [transcriptionToPublish, setTranscriptionToPublish] = useState(
    {} as Bixby.Models.AdminCompanyTranscription | undefined
  );

  const getTranscriptionsAsync = async companyId => {
    setIsLoading(true);
    setTranscriptionsError(undefined);
    try {
      const transcriptions: Bixby.Models.AdminCompanyTranscription[] = await (companyId
        ? TranscriptionsAdminService.getTranscriptionsByCompanyId(companyId)
        : TranscriptionsAdminService.getTranscriptions());
      setTranscriptions(transcriptions);
    } catch (e: any) {
      setTranscriptionsError('Error retrieving transcriptions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTranscriptionsAsync(companyId);
  }, [companyId]);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = (params: any) => {
    if (
      params.column.colId === 'plannerTranscriptionCreateId' ||
      params.column.colId === 'plannerTranscriptionApproveId'
    ) {
      return [];
    }

    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const handleCreateApprovalTask = (
    ev: React.MouseEvent<HTMLButtonElement>,
    transcription: Bixby.Models.AdminCompanyTranscription
  ) => {
    ev.preventDefault();
    setCurrentTranscription(transcription);
    setIsCreatingApprovalTask(true);
  };

  const handleCloseCreateApprovalTaskModal = async () => {
    setIsCreatingApprovalTask(false);
    setCurrentTranscription(undefined);
    await getTranscriptionsAsync(companyId);
  };

  const detailCellRendererParams = useMemo(() => {
    return {
      detailGridOptions: {
        columnDefs: [
          {
            field: 'newsLenderCallEntry',
            headerName: 'News Lender Call Entry',
            autoHeight: true,
            suppressMenu: true,
            sortable: false,
            resizable: false,
            wrapText: true,
            cellRenderer: (params: any) => {
              if (Bixby.Utils.isNullOrEmpty(params.value)) {
                return (
                  <div className="transcript-details error">Error retrieving transcript news lender call entry.</div>
                );
              } else {
                return <div className="transcript-details" dangerouslySetInnerHTML={{ __html: params.value }}></div>;
              }
            },
          },
        ],
        defaultColDef: {
          flex: 1,
        },
        headerHeight: 32,
      },
      getDetailRowData: params => {
        setTimeout(async () => {
          try {
            const transcriptionDetail: Bixby.Models.AdminCompanyTranscription =
              await TranscriptionsAdminService.getById(params.data.transcriptionId);
            params.successCallback([transcriptionDetail]);
          } catch (e) {
            params.successCallback([null]);
          }
        }, 1000);
      },
    } as IDetailCellRendererParams<
      Bixby.Models.AdminCompanyTranscription,
      Bixby.Models.AdminCompanyTranscription | null
    >;
  }, []);

  const publishButtonCellRenderer = (props: any) => {
    const handlePublishButton = () => {
      setTranscriptionToPublish(props.data);
      setPublishModalActive(true);
      return Promise.resolve();
    };

    if (
      props.data['transcriptionStateDisplay'].toLowerCase() ===
        Bixby.Constants.TRANSCRIPTION_STATE_PUBLISHED.toLowerCase() &&
      ((props.data['hasPrivateDeclaration'] === false && !isCurrentCompanyMnpi) ||
        (props.data['hasPrivateDeclaration'] === true && isCurrentCompanyMnpi))
    ) {
      return <PublishButton row={props.data} publisher={handlePublishButton} />;
    } else {
      return null;
    }
  };

  const handlePublishTranscription = async () => {
    if (transcriptionToPublish && !Bixby.Utils.isNullOrEmpty(transcriptionToPublish)) {
      try {
        await TranscriptionsAdminService.republishTranscriptionToCreditsights(transcriptionToPublish.transcriptionId);
        if (dispatchToastSuccess) {
          await dispatchToastSuccess('Transcription published');
        }
      } catch (error: any) {
        if (dispatchToastError) {
          await dispatchToastError('Could not publish transcription');
        }
      }

      setTranscriptionToPublish(undefined);
    }

    setPublishModalActive(false);
  };

  const handleClosePublishModal = () => {
    setPublishModalActive(false);
    setTranscriptionToPublish(undefined);
  };

  if (!Bixby.Utils.isNullOrEmpty(transcriptionsError)) {
    return <p className="error">{transcriptionsError}</p>;
  } else if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <>
        {transcriptions && !Bixby.Utils.isNullOrEmpty(transcriptions) ? (
          <div>
            <div className="transcripts-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    masterDetail={true}
                    getContextMenuItems={getContextMenuItems}
                    detailCellRendererParams={detailCellRendererParams}
                    detailRowAutoHeight={true}
                    onGridReady={onGridReady}
                    rowData={transcriptions}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                    }}
                    rowHeight={32}
                    headerHeight={32}
                    components={{
                      publishButtonCellRenderer,
                    }}
                    rowClassRules={{
                      error: params => {
                        if (!params.data) return false;
                        return (
                          params.data['transcriptionStateDisplay'] === Bixby.Constants.TRANSCRIPTION_STATE_PAST_ERROR
                        );
                      },
                      'past-due': params => {
                        if (!params.data) return false;
                        return (
                          params.data['transcriptionStateDisplay'] === Bixby.Constants.TRANSCRIPTION_STATE_PAST_DUE
                        );
                      },
                    }}
                    columnDefs={[
                      {
                        maxWidth: 225,
                        cellRenderer: 'publishButtonCellRenderer',
                        hide: !isCreditSightsTranscriptionsIntegrationEnabled,
                        headerName: 'Republish to CreditSights',
                        cellClass: 'align-center',
                      },
                      {
                        field: 'companyName',
                        filter: 'agTextColumnFilter',
                        headerName: 'Company',
                        cellRenderer: 'agGroupCellRenderer',
                        minWidth: 250,
                        pinned: 'left',
                        cellClass: 'align-left',
                      },
                      { field: 'companyBixbyId', filter: 'agTextColumnFilter', headerName: 'Bixby ID' },
                      { field: 'companyDataSiteName', filter: 'agTextColumnFilter', headerName: 'Data Site Name' },
                      {
                        field: 'newsLenderStartDate',
                        filter: 'agDateColumnFilter',
                        headerName: 'Due Date',
                        cellRenderer: (params: any) => {
                          return dayjs(params.value).format('MM/DD/YYYY HH:mm');
                        },
                      },
                      { hide: true, field: 'newsLenderTimezone', filter: 'agTextColumnFilter', headerName: 'Timezone' },
                      {
                        hide: true,
                        field: 'transcriptionReplayGoodFrom',
                        filter: 'agDateColumnFilter',
                        headerName: 'Replay Good From',
                        cellRenderer: (params: any) => {
                          return dayjs(params.value).format('MM/DD/YYYY HH:mm');
                        },
                      },
                      {
                        hide: true,
                        field: 'transcriptionReplayGoodUntil',
                        filter: 'agDateColumnFilter',
                        headerName: 'Replay Good Until',
                        cellRenderer: (params: any) => {
                          return dayjs(params.value).format('MM/DD/YYYY HH:mm');
                        },
                      },
                      {
                        field: 'transcriptionStateDisplay',
                        headerName: 'Status',
                        filter: 'agTextColumnFilter',
                        cellClassRules: {
                          'pending-approval': params => {
                            if (!params.data) return false;
                            return (
                              params.data['transcriptionStateDisplay'] ===
                              Bixby.Constants.TRANSCRIPTION_STATE_PENDING_APPROVAL
                            );
                          },
                        },
                        cellRenderer: (params: any) => {
                          const draft = Bixby.Enums.TranscriptionStatus.Draft.toLowerCase();
                          const published = Bixby.Constants.TRANSCRIPTION_STATE_PUBLISHED.toLowerCase();

                          return params.value.toLowerCase() === published &&
                            params.data['transcriptionStatus'].toLowerCase() === draft
                            ? `${params.value} - ${Bixby.Enums.TranscriptionStatus.Draft.toUpperCase()}`
                            : params.value;
                        },
                      },
                      {
                        hide: true,
                        field: 'transcriptionAudioFileState',
                        headerName: 'Audio State',
                        filter: 'agTextColumnFilter',
                      },
                      {
                        hide: true,
                        field: 'transcriptionTextFileState',
                        headerName: 'Text State',
                        filter: 'agTextColumnFilter',
                      },
                      {
                        hide: true,
                        field: 'transcriptionPublishedDate',
                        filter: 'agDateColumnFilter',
                        headerName: 'Published Date',
                        cellRenderer: (params: any) => {
                          return params.value ? dayjs(params.value).format('MM/DD/YYYY HH:mm') : '';
                        },
                      },
                      {
                        field: 'plannerTranscriptionCreateId',
                        headerName: 'Pending Task',
                        filter: 'agTextColumnFilter',
                        cellRenderer: (params: any) => {
                          return Bixby.Utils.isNullOrEmpty(params.data['plannerTranscriptionCreateId']) ||
                            (params.data['hasPrivateDeclaration'] === false && isCurrentCompanyMnpi) ? (
                            ''
                          ) : (
                            <a
                              className="link_underline"
                              target="_blank"
                              href={Bixby.Configuration.plannerTaskUrl + params.data['plannerTranscriptionCreateId']}>
                              {params.data['plannerTranscriptionCreateId']}
                            </a>
                          );
                        },
                      },
                      {
                        field: 'plannerTranscriptionApproveId',
                        headerName: 'Needs Approval Task',
                        filter: 'agTextColumnFilter',
                        cellRenderer: (params: any) => {
                          const hasPrivateDeclaration = params.data?.hasPrivateDeclaration;
                          const isPendingApproval =
                            params.data?.transcriptionStateDisplay ===
                            Bixby.Constants.TRANSCRIPTION_STATE_PENDING_APPROVAL;
                          const noApproveTask = Bixby.Utils.isNullOrEmpty(params.data?.plannerTranscriptionApproveId);
                          const showCreateApprovalButton =
                            noApproveTask &&
                            isPendingApproval &&
                            ((hasPrivateDeclaration === false && !isCurrentCompanyMnpi) ||
                              hasPrivateDeclaration === true);

                          if (showCreateApprovalButton) {
                            return (
                              <BixButton
                                useSmall
                                type="button"
                                useLoading
                                disabled={
                                  isCreatingApprovalTask &&
                                  currentTranscription?.transcriptionId === params.data.transcriptionId
                                }
                                onClick={e => handleCreateApprovalTask(e, params.data)}>
                                Create Approval Task
                              </BixButton>
                            );
                          }

                          if (hasPrivateDeclaration === false && isCurrentCompanyMnpi) {
                            return '';
                          }

                          return (
                            <a
                              className="link_underline"
                              target="_blank"
                              href={Bixby.Configuration.plannerTaskUrl + params.data['plannerTranscriptionApproveId']}>
                              {params.data['plannerTranscriptionApproveId']}
                            </a>
                          );
                        },
                        valueGetter: params => {
                          return params.data?.plannerTranscriptionApproveId;
                        },
                      },
                      { field: 'processingNotes', filter: 'agTextColumnFilter', headerName: 'Processing Notes' },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
            {isCreditSightsTranscriptionsIntegrationEnabled && transcriptionToPublish && (
              <Modal title="" active={publishModalActive} onClose={handleClosePublishModal}>
                <p>Are you sure you want to republish to CreditSights?</p>
                <div className="button_side_wrap">
                  <BixButton type="button" onClick={handlePublishTranscription}>
                    Publish Transcription
                  </BixButton>
                  <BixButton light type="button" onClick={handleClosePublishModal}>
                    Cancel
                  </BixButton>
                </div>
              </Modal>
            )}
          </div>
        ) : (
          <NoDataFound dataType="transcriptions" />
        )}
        <CreateApprovalTask
          currentTranscription={currentTranscription}
          isCreatingApprovalTask={isCreatingApprovalTask}
          onCloseModal={handleCloseCreateApprovalTaskModal}
        />
      </>
    );
  }
};

export default TranscriptsGrid;
